import { CPagination } from '@coreui/react';
import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment-timezone';
import { GoPlus } from 'react-icons/go';
import { FaBell, FaInfoCircle, FaTrash } from 'react-icons/fa';
import { RiPencilFill } from 'react-icons/ri';

import Table from '../../components/Table';
import { StyledCard, StyledCardBody } from '../../components/Cards';
import {
  HeaderButtonContainer,
  HeaderMenu,
  HeaderTitle,
  SearchCloseIcon,
  SearchContainer,
  SearchIcon,
  TableContainer
} from '../../components/tables/NewTableComponents';
import {
  StyledDropdownItem,
  StyledDropdownMenu,
  StyledTableDropdown,
  StyledTableDropdownSeparator,
  StyledTableDropdownToggle
} from '../../components/tables/TableDropdownComponents';
import { ReactComponent as MoreButtonIcon } from '../../assets/icons/more_button.svg';
import DeleteModal from '../../components/DeleteModal';
import ConfirmModal from '../../components/ConfirmModal';
import { Badge } from '../../components/ui/Badge';
import { ButtonBodyVariant } from '../../components/ui/ButtonVariant';
import { TableColumn, TableColumnTitle } from '../../components/tables/TableComponents';
import { Form, FormControl, FormField, FormItem } from '../../components/ui/Form';
import { Input } from '../../components/ui/Input';
import { changeOpenedModal } from '../../redux/actions/headerModal';
import { deleteEvent, editEvent, notifyEvent } from '../../redux/actions/events';
import { setToast } from '../../redux/actions/window';
import { openModal } from '../../helper/modals';
import { TIME_ZONES } from '../../helper/constants';
import { DATE_FORMATS } from '../users/constant';
import { useGetEvents } from '../../hooks/useGetEvents';
import { SettingsModalBoldText } from '../settings/SettingsModals';
import { FeatureSwitch } from '../offers/styles';
import { EVENT_STATUS, EVENT_STATUS_LABEL, isEventVisible } from './constants';

const EVENT_FIELDS = [
  { key: 'title', label: 'Title' },
  { key: 'sub_heading', label: 'Subheading' },
  { key: 'event_date', label: 'Date' },
  { key: 'location', label: 'Location' },
  { key: 'time', label: 'Time' },
  { key: 'status', label: 'Status' },
  { key: 'pinned', label: 'Pinned' },
  { key: 'notification', label: 'Notification' }
];

const DEFAULT_PAGE_SIZE = 20;
const EVENT_TIME_PARSE_FORMATS = [
  DATE_FORMATS.HH_mm_ss,
  DATE_FORMATS.HH_mm,
  DATE_FORMATS.hh_mm_A,
  DATE_FORMATS.h_mm_A,
  moment.ISO_8601
];

export const parseEventTime = (value) => {
  if (!value) {
    return null;
  }
  if (moment.isMoment(value)) {
    return value;
  }
  const parsed = moment(value, EVENT_TIME_PARSE_FORMATS, true);
  return parsed.isValid() ? parsed : null;
};

export const isValidEventTimeRange = (startValue, endValue) => {
  const start = parseEventTime(startValue);
  const end = parseEventTime(endValue);
  
  if (!start || !end) {
    return !startValue || !endValue;
  }

  const startTimeStr = start.format(DATE_FORMATS.HH_mm_ss);
  const endTimeStr = end.format(DATE_FORMATS.HH_mm_ss);

  if (endTimeStr === '00:00:00') {
    return true;
  }

  return endTimeStr > startTimeStr;
};

export const formatEventTimeForApi = (value) => {
  const parsed = parseEventTime(value);
  return parsed ? parsed.format(DATE_FORMATS.HH_mm_ss) : '';
};

export const formatEventTime = (value) => {
  const parsed = parseEventTime(value);
  return parsed ? parsed.format(DATE_FORMATS.hh_mm_A) : '';
};

export const isAllDayEvent = (startTime, endTime) => {
  const startParsed = parseEventTime(startTime);
  const endParsed = parseEventTime(endTime);
  
  if (startParsed && endParsed) {
    const startStr = startParsed.format(DATE_FORMATS.HH_mm_ss);
    const endStr = endParsed.format(DATE_FORMATS.HH_mm_ss);
    
    return startStr === '00:00:00' && endStr === '00:00:00';
  }
  
  return false;
};

export const formatEventTimeRange = (startTime, endTime) => {
  if (isAllDayEvent(startTime, endTime)) {
    return 'All Day';
  }
  
  const start = formatEventTime(startTime);
  const end = formatEventTime(endTime);
  if (start && end) {
    return `${start}–${end}`;
  }
  return start || end || '-';
};

export const hasEventStarted = (eventDate, startTime, buildingState) => {
  if (!buildingState || !eventDate || !startTime) {
    return false;
  }
  const timezone = TIME_ZONES.get(String(buildingState).trim().toUpperCase());
  const datePart = moment(eventDate).format(DATE_FORMATS.YYYY_MM_DD);
  const start = parseEventTime(startTime);
  if (!timezone || !start) {
    return false;
  }

  const timePart = start.format(DATE_FORMATS.HH_mm_ss);
  const eventStart = moment.tz(
    `${datePart} ${timePart}`,
    `${DATE_FORMATS.YYYY_MM_DD} ${DATE_FORMATS.HH_mm_ss}`,
    true,
    timezone
  );
  if (!eventStart.isValid()) {
    return false;
  }

  return !eventStart.isAfter(moment().tz(timezone));
};

const Events = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const globalSelectedBuilding = useSelector(
    (state) => state.building.globalSelectedBuilding
  );
  const [activePage, setActivePage] = useState(1);
  const [appliedSearch, setAppliedSearch] = useState('');
  const [togglingIds, setTogglingIds] = useState(() => new Set());
  const [showModal, setShowModal] = useState({ id: null, show: false });
  const { modal, data: modalData } = useSelector((state) => state.headerModal);
  const notifyTarget = modal === 'notifyEvent' ? modalData : null;
  const [isNotifying, setIsNotifying] = useState(false);

  const form = useForm({
    mode: 'onSubmit',
    defaultValues: {
      search: ''
    }
  });

  const {
    data,
    totalPages,
    isLoading,
    hasBuilding,
    isEventsEnabled,
    refetch
  } = useGetEvents({
    page: activePage,
    search: appliedSearch
  });

  useEffect(() => {
    setActivePage(1);
    setAppliedSearch('');
    form.reset({ search: '' });
  }, [hasBuilding, isEventsEnabled]);

  useEffect(() => {
    if (activePage > totalPages) {
      setActivePage(totalPages);
    }
  }, [activePage, totalPages]);

  const onSubmit = (values) => {
    setAppliedSearch(values.search?.trim() || '');
    setActivePage(1);
  };

  const handleCreate = () => {
    dispatch(changeOpenedModal({ modal: 'addEditEvent' }));
  };

  const handleEdit = (event) => {
    if (hasEventStarted(event?.event_date, event?.start_time, globalSelectedBuilding?.state)) {
      return;
    }
    dispatch(
      changeOpenedModal({
        modal: 'addEditEvent',
        data: { id: event.id }
      })
    );
  };

  const handleDetails = (event) => {
    dispatch(
      changeOpenedModal({
        modal: 'eventDetails',
        data: { id: event.id }
      })
    );
  };

  const handleSpecificColClick = (item, _index, col) => {
    if (col === 'edit' || col === 'status' || col === 'pinned') {
      return;
    }
    handleDetails(item);
  };

  const handleDelete = () => {
    dispatch(
      deleteEvent({
        id: showModal.id,
        success: (res) => {
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: res?.data?.residents_notified
                ? t('Event deleted and residents have been notified of the cancellation.')
                : t('Event deleted successfully.')
            })
          );
          refetch();
          setShowModal({ id: null, show: false });
        },
        fail: () => {
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: t('Failed to delete event!'),
              modal: 'errorToast'
            })
          );
        }
      })
    );
  };

  const openNotify = (item) => {
    dispatch(
      changeOpenedModal({
        modal: 'notifyEvent',
        data: { id: item.id, notification: item.notification }
      })
    );
  };

  const closeNotify = () => {
    dispatch(changeOpenedModal({ modal: '' }));
  };

  const getNotifyDescription = (target) => {
    const { notified, notified_date: notifiedDate } = target?.notification || {};
    if (notified) {
      return t('Residents were already notified about this event. Do you want to notify them again?');
    }
    if (notifiedDate) {
      return t('This event has been updated. Do you want to notify residents about it?');
    }
    return t('Residents have never been notified about this event. Do you want to notify them?');
  };

  const handleNotify = () => {
    setIsNotifying(true);
    dispatch(
      notifyEvent({
        id: notifyTarget.id,
        success: () => {
          setIsNotifying(false);
          closeNotify();
          refetch();
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: t('Residents have been successfully notified.')
            })
          );
        },
        fail: (res) => {
          setIsNotifying(false);
          closeNotify();
          refetch();
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: res?.data?.message || t('Failed to notify residents!'),
              modal: 'errorToast'
            })
          );
        }
      })
    );
  };

  const setToggling = (id, isToggling) => {
    setTogglingIds((prev) => {
      const next = new Set(prev);
      if (isToggling) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  };

  const toggleStatus = (item) => {
    if (
      !item?.id ||
      togglingIds.has(`status-${item.id}`) ||
      hasEventStarted(item?.event_date, item?.start_time, globalSelectedBuilding?.state)
    ) {
      return;
    }

    const isVisible = isEventVisible(item);
    const nextStatus = isVisible ? EVENT_STATUS.HIDDEN : EVENT_STATUS.VISIBLE;

    setToggling(`status-${item.id}`, true);
    dispatch(
      editEvent({
        id: item.id,
        body: {
          status: nextStatus,
          status_id: nextStatus,
          buildings: [{ building_id: globalSelectedBuilding?.id, visible: !isVisible }]
        },
        success: (res) => {
          setToggling(`status-${item.id}`, false);
          refetch();
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: isVisible
                ? t('Event has been successfully hidden.')
                : t('Event has been successfully made visible.')
            })
          );
          if (!isVisible) {
            openNotify(res.data);
          }
        },
        fail: () => {
          setToggling(`status-${item.id}`, false);
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: t('Failed to update event status!'),
              modal: 'errorToast'
            })
          );
        }
      })
    );
  };

  const togglePinned = (item) => {
    if (
      !item?.id ||
      togglingIds.has(`pinned-${item.id}`) ||
      hasEventStarted(item?.event_date, item?.start_time, globalSelectedBuilding?.state)
    ) {
      return;
    }

    const nextPinned = !item.pinned;

    setToggling(`pinned-${item.id}`, true);
    dispatch(
      editEvent({
        id: item.id,
        body: {
          pinned: nextPinned,
          buildings: [{ building_id: globalSelectedBuilding?.id, pinned: nextPinned }]
        },
        success: () => {
          setToggling(`pinned-${item.id}`, false);
          refetch();
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: nextPinned
                ? t('Event has been successfully pinned.')
                : t('Event has been successfully unpinned.')
            })
          );
        },
        fail: () => {
          setToggling(`pinned-${item.id}`, false);
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: t('Failed to update event pin!'),
              modal: 'errorToast'
            })
          );
        }
      })
    );
  };

  const memoFields = useMemo(
    () => [
      ...EVENT_FIELDS.map((column) => ({
        key: column.key,
        label: (
          <TableColumn>
            <TableColumnTitle>{column.label}</TableColumnTitle>
          </TableColumn>
        )
      })),
      { key: 'edit', label: '' }
    ],
    []
  );

  const emptyMessage = !hasBuilding
    ? t('Please select a building')
    : isEventsEnabled
      ? t('No events')
      : t('Feature is disabled for this building');

  return (
    <StyledCard>
      <StyledCardBody>
        <TableContainer>
          <HeaderMenu>
            <HeaderTitle>{t('Events')}</HeaderTitle>
            {isEventsEnabled && (
              <HeaderButtonContainer>
                <ButtonBodyVariant onClick={handleCreate}>
                  <GoPlus size={20} className="mr-1" /> {t('Add New')}
                </ButtonBodyVariant>
              </HeaderButtonContainer>
            )}
          </HeaderMenu>

          {isEventsEnabled && (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)}>
                <SearchContainer>
                  <FormField
                    control={form.control}
                    name="search"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl style={{ position: 'relative' }}>
                          <SearchIcon size={18} />
                          <Input
                            {...field}
                            placeholder={t('Search')}
                            style={{ paddingLeft: '40px', paddingRight: '40px' }}
                            onChange={(e) => {
                              field.onChange(e);
                              if (e.target.value.length === 0) {
                                setAppliedSearch('');
                                setActivePage(1);
                              }
                            }}
                          />
                          <SearchCloseIcon
                            size={18}
                            disabled={!field.value}
                            onClick={() => {
                              if (!field.value) {
                                return;
                              }
                              form.setValue(field.name, '');
                              setAppliedSearch('');
                              setActivePage(1);
                            }}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <ButtonBodyVariant type="submit">{t('Search')}</ButtonBodyVariant>
                </SearchContainer>
              </form>
            </Form>
          )}

          <Table
            items={isEventsEnabled ? data : []}
            useNewStyle
            itemsPerPage={DEFAULT_PAGE_SIZE}
            loading={isEventsEnabled && isLoading}
            fields={memoFields}
            onRowClick={handleSpecificColClick}
            noItemsView={{
              noItems: emptyMessage
            }}
            scopedSlots={{
              event_date: (item) => (
                <td>
                  {item.event_date
                    ? moment(item.event_date).format(DATE_FORMATS.MMM_DD_YYYY)
                    : '-'}
                </td>
              ),
              time: (item) => (
                <td>{formatEventTimeRange(item.start_time, item.end_time)}</td>
              ),
              sub_heading: (item) => <td>{item.sub_heading || '-'}</td>,
              location: (item) => <td>{item.location || '-'}</td>,
              status: (item) => {
                const isVisible = isEventVisible(item);
                const started = hasEventStarted(item?.event_date, item?.start_time, globalSelectedBuilding?.state);
                return (
                  <td className="status-cell">
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start'
                      }}>
                      <SettingsModalBoldText cssOverride="font-size: 12px; line-height: 16px;">
                        {t(EVENT_STATUS_LABEL[isVisible ? EVENT_STATUS.VISIBLE : EVENT_STATUS.HIDDEN])}
                      </SettingsModalBoldText>
                      <FeatureSwitch
                        size="sm"
                        checked={isVisible}
                        disabled={started || togglingIds.has(`status-${item.id}`)}
                        onChange={() => toggleStatus(item)}
                        value={isVisible}
                        shape="pill"
                        color="primary"
                      />
                    </div>
                  </td>
                );
              },
              pinned: (item) => {
                const isPinned = Boolean(item.pinned);
                const started = hasEventStarted(item?.event_date, item?.start_time, globalSelectedBuilding?.state);
                return (
                  <td className="status-cell">
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start'
                      }}>
                      <SettingsModalBoldText cssOverride="font-size: 12px; line-height: 16px;">
                        {isPinned ? t('Yes') : t('No')}
                      </SettingsModalBoldText>
                      <FeatureSwitch
                        size="sm"
                        checked={isPinned}
                        disabled={started || togglingIds.has(`pinned-${item.id}`)}
                        onChange={() => togglePinned(item)}
                        value={isPinned}
                        shape="pill"
                        color="primary"
                      />
                    </div>
                  </td>
                );
              },
              notification: (item) => (
                <td>
                  {item.notification?.notified ? (
                    <Badge variant="complete">{t('Notified')}</Badge>
                  ) : (
                    <Badge variant="pending">
                      {t(item.notification?.notified_date ? 'Not notified' : 'Never notified')}
                    </Badge>
                  )}
                </td>
              ),
              edit: (item) => {
                const started = hasEventStarted(item?.event_date, item?.start_time, globalSelectedBuilding?.state);
                return (
                  <td className="text-center edit-width edit-container">
                    <StyledTableDropdown>
                      <StyledTableDropdownToggle>
                        <MoreButtonIcon />
                      </StyledTableDropdownToggle>
                      <StyledDropdownMenu>
                        <StyledDropdownItem onClick={() => handleDetails(item)}>
                          <FaInfoCircle size={16} className="mr-2" />
                          {t('Details')}
                        </StyledDropdownItem>
                        {!started && (
                          <>
                            {isEventVisible(item) && (
                              <>
                                <StyledTableDropdownSeparator />
                                <StyledDropdownItem onClick={() => openNotify(item)}>
                                  <FaBell size={16} className="mr-2" />
                                  {t('Notify Residents')}
                                </StyledDropdownItem>
                              </>
                            )}
                            <StyledTableDropdownSeparator />
                            <StyledDropdownItem onClick={() => handleEdit(item)}>
                              <RiPencilFill size={16} className="mr-2" />
                              {t('Edit')}
                            </StyledDropdownItem>
                          </>
                        )}
                        <StyledTableDropdownSeparator />
                        <StyledDropdownItem
                          onClick={() => {
                            setShowModal({ show: true, id: item.id });
                            openModal('deleteModal');
                          }}>
                          <FaTrash size={16} className="mr-2" />
                          {t('Delete')}
                        </StyledDropdownItem>
                      </StyledDropdownMenu>
                    </StyledTableDropdown>
                  </td>
                );
              }
            }}
          />

          {isEventsEnabled && (
            <CPagination
              activePage={activePage}
              pages={totalPages}
              onActivePageChange={(page) => {
                if (page) {
                  setActivePage(page);
                }
              }}
            />
          )}

          {showModal.show && (
            <DeleteModal
              type="Event"
              action={handleDelete}
              setShowModal={setShowModal}
              modalState={showModal}
              description={t('Are you sure you want to delete this event?')}
            />
          )}

          <ConfirmModal
            show={Boolean(notifyTarget)}
            title={t('Notify Residents')}
            description={getNotifyDescription(notifyTarget)}
            confirmText={t('Notify now')}
            cancelText={t('Later')}
            onConfirm={handleNotify}
            onCancel={closeNotify}
            disabled={isNotifying}
          />
        </TableContainer>
      </StyledCardBody>
    </StyledCard>
  );
};

export default Events;
